import {
  GroupIds,
  SectionIds,
} from '@bupa-kkv/features/company-creation/types';
import { queryKeyStore } from '@bupa-monorepo/shared-frontend/query-key-store';
import {
  getElementFromGroup,
  getPageConfigurationElement,
  getPageConfigurationGroup,
  isDefined,
} from '@bupa-monorepo/shared-frontend/utils';
import {
  IConfigurationGroup,
  IPageConfiguration,
} from '@bupa-monorepo/shared-types/page-configuration-types';
import { useQuery } from '@tanstack/react-query';
import { fetchConfiguration } from '../api/configuration.api';
import {
  HelpPageConfiguration,
  ServiceCardConfiguration,
} from '../types/configuration.types';

export const useServiceCardConfiguration = () => {
  return useQuery({
    queryKey: [queryKeyStore.companyCreation.serviceCardsConfig.queryKey],
    queryFn: () => fetchConfiguration('selfEmploymentServiceCardsPage'),
    select: mapResponse,
  });
};

const mapResponse = (
  pageConfiguration: IPageConfiguration
): HelpPageConfiguration => {
  return {
    taxationCardConfig: mapCardConfig({
      pageConfiguration,
      sectionId: SectionIds.SERVICE_CARDS_BLOCKS,
      groupId: GroupIds.TAXATION_HELPER,
      bulletpointGroupId: GroupIds.TAXATION_HELPER,
      addDescId: GroupIds.TAXATION_HELPER_ADDITIONAL,
    }),
    callbackCardConfig: mapCardConfig({
      pageConfiguration,
      sectionId: SectionIds.SERVICE_CARDS_BLOCKS,
      groupId: GroupIds.CALLBACK,
      bulletpointGroupId: GroupIds.CALLBACK,
      addDescId: GroupIds.CALLBACK_ADDITIONAL,
    }),
  };
};

const mapCardConfig = ({
  pageConfiguration,
  sectionId,
  groupId,
  bulletpointGroupId,
  addDescId,
}: {
  pageConfiguration: IPageConfiguration;
  sectionId: string;
  groupId: string;
  bulletpointGroupId: string;
  addDescId: string;
}): ServiceCardConfiguration => ({
  title:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: groupId,
      elementId: 'title',
    })?.value ?? '',
  price:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: groupId,
      elementId: 'price',
    })?.value ?? '',
  bulletpoints: mapSectionInstructionSteps({
    pageConfiguration,
    sectionId,
    groupId: bulletpointGroupId,
  }),
  additionalDescription:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: addDescId,
      elementId: 'description',
    })?.value ?? '',
});

const mapSectionInstructionSteps = ({
  pageConfiguration,
  sectionId,
  groupId,
}: {
  pageConfiguration: IPageConfiguration;
  sectionId: string;
  groupId: string;
}): string[] => {
  return [
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}1`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}2`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}3`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}4`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}5`,
    }),
  ]
    .filter(isDefined)
    .filter((group) => group?.isVisible)
    .map(mapInstructionStep);
};

const mapInstructionStep = (group: IConfigurationGroup) => {
  return getElementFromGroup({ group, elementId: 'description' })?.value ?? '';
};
